import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Database, ExternalLink, AlertCircle, Rocket, Link2, Unlink, Github, Triangle, Key, Plus, Trash2, Copy } from 'lucide-react';
import { PxMark } from '@/components/PxMark';
import { useIntegrations } from '@/hooks/useIntegrations';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { CHAT_FUNCTION } from '@/lib/chatFunction';

interface ProjectSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  chatId?: string;
  versionId?: string;
  files?: Array<{ name: string; content: string }>;
  demoUrl?: string;
}

/** Label de section mono uppercase 11 entre hairlines (grammaire OFFSET). */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="shrink-0 font-mono text-[11px] uppercase tracking-[0.1em] text-faint">
        {children}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

/** Badge d'état mono 10 uppercase. */
function StateBadge({ tone = 'faint', children }: { tone?: 'ok' | 'faint' | 'err'; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em]',
        tone === 'ok' && 'bg-ok/10 text-ok',
        tone === 'faint' && 'bg-secondary text-faint',
        tone === 'err' && 'bg-err/10 text-err',
      )}
    >
      {children}
    </span>
  );
}

/** Row d'identité de service : carré 32px avec icône + nom + badge. */
function ServiceRow({
  icon: Icon,
  name,
  meta,
  badges,
  action,
}: {
  icon: React.ComponentType<{ className?: string }>;
  name: string;
  meta?: string;
  badges?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-border bg-secondary">
          <Icon className="h-4 w-4 text-foreground" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[13px] font-medium text-foreground">
            {name}
            {badges}
          </div>
          {meta && <p className="truncate text-[12px] text-muted-foreground">{meta}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

/** Key-value row : label encre à gauche, valeur mono à droite + copier. */
function KeyValueRow({ label, value, href }: { label: string; value: string; href?: string }) {
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    toast.success('Copié dans le presse-papiers');
  };

  return (
    <div className="flex items-center justify-between gap-3 border border-border bg-secondary px-3 py-2">
      <span className="shrink-0 text-[13px] font-medium text-foreground">{label}</span>
      <div className="flex min-w-0 items-center gap-1">
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate font-mono text-[12px] text-muted-foreground underline-offset-[3px] hover:text-foreground hover:underline"
          >
            {value}
          </a>
        ) : (
          <span className="truncate font-mono text-[12px] text-muted-foreground">{value}</span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={handleCopy}
          aria-label="Copier"
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function ProjectSettingsDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  chatId,
  versionId: propVersionId,
  files = [],
  demoUrl,
}: ProjectSettingsDialogProps) {
  const {
    isLoading,
    isDeploying,
    projectIntegrations,
    supabaseOrganizations,
    githubConnection,
    supabaseConnection,
    fetchIntegrations,
    checkSupabaseConnection,
    checkGitHubConnection,
    connectGitHub,
    disconnectGitHub,
    connectSupabase,
    disconnectSupabase,
    fetchProjectIntegrations,
    createSupabaseProject,
    deployToVercelWithClaim,
    listEnvVars,
    createEnvVars,
    deleteEnvVars,
    isSupabaseConnected,
    isSupabaseOAuthConnected,
    isGitHubConnected,
  } = useIntegrations();

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [isCreatingSupabase, setIsCreatingSupabase] = useState(false);
  const [isConnectingGitHub, setIsConnectingGitHub] = useState(false);
  const [isConnectingSupabase, setIsConnectingSupabase] = useState(false);
  const [versionId, setVersionId] = useState<string | undefined>(propVersionId);
  const [isFetchingVersion, setIsFetchingVersion] = useState(false);
  const [vercelResult, setVercelResult] = useState<{
    deploymentUrl?: string;
    webUrl?: string;
    inspectorUrl?: string;
    claimUrl?: string;
    message?: string;
    status?: string;
    error?: string;
  } | null>(null);

  // Env vars state
  const [envVarsList, setEnvVarsList] = useState<Array<{ id: string; key: string; value?: string }>>([]);
  const [isLoadingEnvVars, setIsLoadingEnvVars] = useState(false);
  const [newEnvKey, setNewEnvKey] = useState('');
  const [newEnvValue, setNewEnvValue] = useState('');

  // Fetch latest version if chatId exists but versionId is missing
  const fetchLatestVersion = useCallback(async () => {
    if (!chatId || propVersionId) return;

    setIsFetchingVersion(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Le serveur attend projectId (cf. useVersionHistory.ts), pas chatId.
      const response = await supabase.functions.invoke(CHAT_FUNCTION, {
        body: { action: 'listVersions', projectId },
      });

      if (response.data?.versions?.length > 0) {
        // Get the latest completed version
        const latestVersion = response.data.versions.find((v: any) => v.status === 'ready') || response.data.versions[0];
        console.log('Fetched latest version for deployment:', latestVersion.id);
        setVersionId(latestVersion.id);
      }
    } catch (error) {
      console.error('Error fetching latest version:', error);
    } finally {
      setIsFetchingVersion(false);
    }
  }, [chatId, propVersionId, projectId]);

  // Check for connection success from callbacks
  useEffect(() => {
    if (searchParams.get('github') === 'connected') {
      toast.success("GitHub connecté");
      searchParams.delete('github');
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get('supabase') === 'connected') {
      toast.success("Compte Supabase connecté");
      searchParams.delete('supabase');
      setSearchParams(searchParams, { replace: true });
      // Refresh connections
      checkSupabaseConnection();
    }
  }, [searchParams, setSearchParams, checkSupabaseConnection]);

  // Load env vars when dialog opens
  const loadEnvVars = useCallback(async () => {
    if (!projectId) return;
    setIsLoadingEnvVars(true);
    try {
      const vars = await listEnvVars(projectId);
      setEnvVarsList(vars);
    } catch (err) {
      console.error('Failed to load env vars:', err);
    } finally {
      setIsLoadingEnvVars(false);
    }
  }, [projectId, listEnvVars]);

  useEffect(() => {
    if (open) {
      fetchIntegrations();
      checkSupabaseConnection();
      checkGitHubConnection();
      fetchProjectIntegrations(projectId);
      fetchLatestVersion();
      loadEnvVars();
    }
  }, [open, projectId, fetchIntegrations, checkSupabaseConnection, checkGitHubConnection, fetchProjectIntegrations, fetchLatestVersion, loadEnvVars]);

  // Update local versionId when prop changes
  useEffect(() => {
    if (propVersionId) {
      setVersionId(propVersionId);
    }
  }, [propVersionId]);

  useEffect(() => {
    if (supabaseOrganizations.length > 0 && !selectedOrg) {
      setSelectedOrg(supabaseOrganizations[0].id);
    }
  }, [supabaseOrganizations, selectedOrg]);

  const supabaseIntegration = projectIntegrations.find(i => i.provider === 'supabase');
  const vercelIntegration = projectIntegrations.find(i => i.provider === 'vercel');
  const githubIntegration = projectIntegrations.find(i => i.provider === 'github');

  const handleCreateSupabaseProject = async () => {
    if (!selectedOrg) {
      toast.error("Sélectionnez une organisation");
      return;
    }

    setIsCreatingSupabase(true);
    try {
      const result = await createSupabaseProject(projectId, projectName, selectedOrg, files);
      toast.success("Projet Supabase créé", {
        description: result.schemas?.length
          ? `${result.schemas.length} table(s) détectée(s) et créée(s)`
          : "Base de données prête",
      });
    } catch (err) {
      toast.error("Erreur lors de la création du projet Supabase");
    } finally {
      setIsCreatingSupabase(false);
    }
  };

  const handleConnectGitHub = async () => {
    setIsConnectingGitHub(true);
    try {
      await connectGitHub();
    } catch (err) {
      toast.error("Erreur lors de la connexion à GitHub");
      setIsConnectingGitHub(false);
    }
  };

  const handleDisconnectGitHub = async () => {
    const result = await disconnectGitHub();
    if (result.success) {
      toast.success("GitHub déconnecté");
    } else {
      toast.error("Erreur lors de la déconnexion");
    }
  };

  const handleConnectSupabase = async () => {
    setIsConnectingSupabase(true);
    try {
      await connectSupabase();
    } catch (err) {
      toast.error("Erreur lors de la connexion à Supabase");
      setIsConnectingSupabase(false);
    }
  };

  const handleDisconnectSupabase = async () => {
    const result = await disconnectSupabase();
    if (result.success) {
      toast.success("Supabase déconnecté");
    } else {
      toast.error("Erreur lors de la déconnexion");
    }
  };

  const handleDeployVercel = async () => {
    console.log('Deploy clicked - chatId:', chatId, 'versionId:', versionId, 'projectId:', projectId);

    if (!chatId || !versionId) {
      toast.error("Aucune version à déployer", {
        description: "Générez d'abord du code avant de déployer",
      });
      return;
    }

    try {
      // Use deployToVercelWithClaim which deploys with v0 files and creates claim URL
      const result = await deployToVercelWithClaim(chatId, versionId, projectId);
      setVercelResult({
        webUrl: result.webUrl,
        deploymentUrl: result.deploymentUrl,
        claimUrl: result.claimUrl,
        message: result.message,
        status: result.status,
        error: result.error,
      });

      const publicUrl = result.deploymentUrl || result.webUrl;

      if (result.status === 'error') {
        toast.warning("Build en erreur", {
          description: result.claimUrl
            ? "Le build a échoué mais vous pouvez transférer le projet pour corriger"
            : "Vérifiez les logs du build",
          action: publicUrl ? {
            label: "Voir le site",
            onClick: () => window.open(publicUrl, '_blank'),
          } : undefined,
        });
      } else if (result.claimUrl) {
        toast.success("Déploiement réussi", {
          description: publicUrl || "Cliquez pour transférer le projet sur votre compte Vercel",
          action: {
            label: "Transférer",
            onClick: () => window.open(result.claimUrl, '_blank'),
          },
        });
      } else {
        toast.success("Déploiement réussi", {
          description: publicUrl || "Votre projet est en ligne",
          action: publicUrl ? {
            label: "Voir le site",
            onClick: () => window.open(publicUrl, '_blank'),
          } : undefined,
        });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur lors du déploiement");
    }
  };

  const canDeploy = chatId && versionId;
  const publicVercelUrl = vercelResult?.deploymentUrl || vercelResult?.webUrl || vercelIntegration?.external_project_url;
  const claimUrl = vercelResult?.claimUrl || (vercelIntegration?.metadata as Record<string, string>)?.claimUrl;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-hairline-strong bg-card sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Paramètres du projet</DialogTitle>
          <DialogDescription>
            Gérez les intégrations et le déploiement de votre projet
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="deploy" className="mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="deploy" className="flex items-center gap-2">
              <Rocket className="h-4 w-4" />
              Déploiement
            </TabsTrigger>
            <TabsTrigger value="database" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Base de données
            </TabsTrigger>
            <TabsTrigger value="vars" className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              Variables
            </TabsTrigger>
          </TabsList>

          <TabsContent value="deploy" className="mt-6 space-y-6">
            {/* Vercel — hébergement */}
            <div className="space-y-4">
              <SectionLabel>Hébergement</SectionLabel>

              <ServiceRow
                icon={Triangle}
                name="Vercel"
                meta="Déploiement instantané avec Next.js natif"
                badges={
                  (vercelIntegration || vercelResult) ? (
                    vercelResult?.status === 'error'
                      ? <StateBadge tone="err">Build échoué</StateBadge>
                      : <StateBadge tone="ok">En ligne</StateBadge>
                  ) : undefined
                }
              />

              {(vercelIntegration || vercelResult) && publicVercelUrl && (
                <KeyValueRow label="URL" value={publicVercelUrl} href={publicVercelUrl} />
              )}

              {/* Erreur de build */}
              {vercelResult?.status === 'error' && vercelResult?.error && (
                <div className="border-l-2 border-err bg-err/5 p-3">
                  <p className="text-[13px] font-medium text-err">Erreur de build</p>
                  <p className="mt-1 font-mono text-[11px] text-muted-foreground">{vercelResult.error}</p>
                </div>
              )}

              {/* Claim URL */}
              {claimUrl && (
                <div className="border border-border bg-secondary p-3">
                  <p className="text-[13px] font-medium text-foreground">Transférer sur votre compte</p>
                  <p className="mb-2 mt-0.5 text-[12px] text-muted-foreground">
                    Cliquez pour devenir propriétaire du projet Vercel
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(claimUrl, '_blank')}
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Transférer le projet
                  </Button>
                </div>
              )}

              <Button
                className="w-full"
                onClick={handleDeployVercel}
                disabled={isDeploying || !canDeploy}
              >
                {isDeploying ? (
                  <>
                    <PxMark state="generating" size={14} className="mr-2" />
                    Déploiement en cours…
                  </>
                ) : vercelIntegration ? (
                  <>
                    <Rocket className="mr-2 h-4 w-4" />
                    Redéployer
                  </>
                ) : (
                  <>
                    <Triangle className="mr-2 h-4 w-4" />
                    Déployer sur Vercel
                  </>
                )}
              </Button>

              {!canDeploy && (
                <p className="flex items-center gap-1 font-mono text-[11px] text-faint">
                  <AlertCircle className="h-3 w-3" />
                  Générez du code pour pouvoir déployer
                </p>
              )}
            </div>

            {/* GitHub — code source */}
            <div className="space-y-4">
              <SectionLabel>Code source</SectionLabel>

              <ServiceRow
                icon={Github}
                name="GitHub"
                meta={isGitHubConnected
                  ? `@${githubConnection.username}`
                  : "Exporter le code vers votre repository"}
                badges={
                  isGitHubConnected
                    ? <StateBadge tone="ok">Connecté</StateBadge>
                    : <StateBadge tone="faint">Non connecté</StateBadge>
                }
                action={
                  isGitHubConnected ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-err hover:text-err"
                      onClick={handleDisconnectGitHub}
                    >
                      <Unlink className="mr-2 h-4 w-4" />
                      Déconnecter
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={handleConnectGitHub}
                      disabled={isConnectingGitHub}
                    >
                      {isConnectingGitHub ? (
                        <PxMark state="thinking" size={14} />
                      ) : (
                        <>
                          <Link2 className="mr-2 h-4 w-4" />
                          Connecter
                        </>
                      )}
                    </Button>
                  )
                }
              />

              {githubIntegration && (
                <KeyValueRow
                  label="Repo"
                  value={githubIntegration.external_project_url}
                  href={githubIntegration.external_project_url}
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="database" className="mt-6 space-y-6">
            {/* Compte Supabase */}
            <div className="space-y-4">
              <SectionLabel>Compte</SectionLabel>

              <ServiceRow
                icon={Database}
                name="Supabase"
                meta={isSupabaseOAuthConnected
                  ? "Les projets seront créés dans votre compte"
                  : "Connectez-vous pour créer des projets dans votre compte"}
                badges={
                  <>
                    {isSupabaseOAuthConnected && <StateBadge tone="ok">Connecté</StateBadge>}
                    {isSupabaseConnected && !isSupabaseOAuthConnected && <StateBadge tone="faint">Mode admin</StateBadge>}
                    {!isSupabaseConnected && !isSupabaseOAuthConnected && <StateBadge tone="faint">Non connecté</StateBadge>}
                  </>
                }
                action={
                  isSupabaseOAuthConnected ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-err hover:text-err"
                      onClick={handleDisconnectSupabase}
                    >
                      <Unlink className="mr-2 h-4 w-4" />
                      Déconnecter
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={handleConnectSupabase}
                      disabled={isConnectingSupabase}
                    >
                      {isConnectingSupabase ? (
                        <PxMark state="thinking" size={14} />
                      ) : (
                        <>
                          <Link2 className="mr-2 h-4 w-4" />
                          Connecter
                        </>
                      )}
                    </Button>
                  )
                }
              />
            </div>

            {/* Projet lié */}
            <div className="space-y-4">
              <SectionLabel>Projet</SectionLabel>

              {supabaseIntegration ? (
                <>
                  <ServiceRow
                    icon={Database}
                    name={supabaseIntegration.external_project_name}
                    meta="Projet Supabase actif"
                    badges={
                      <>
                        <StateBadge tone="ok">Actif</StateBadge>
                        {(supabaseIntegration.metadata as Record<string, unknown>)?.isOAuthProject && (
                          <StateBadge tone="faint">Votre compte</StateBadge>
                        )}
                      </>
                    }
                    action={
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(supabaseIntegration.external_project_url, '_blank')}
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Dashboard
                      </Button>
                    }
                  />
                  <KeyValueRow
                    label="URL"
                    value={supabaseIntegration.external_project_url}
                    href={supabaseIntegration.external_project_url}
                  />
                </>
              ) : isSupabaseConnected ? (
                <div className="space-y-3">
                  <p className="text-[12px] text-muted-foreground">
                    {isSupabaseOAuthConnected
                      ? "Base de données dans votre compte Supabase"
                      : "Base de données PostgreSQL et API"}
                  </p>

                  <div>
                    <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.1em] text-faint">
                      Organisation
                    </label>
                    <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner une organisation" />
                      </SelectTrigger>
                      <SelectContent>
                        {supabaseOrganizations.map(org => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    className="w-full"
                    onClick={handleCreateSupabaseProject}
                    disabled={isCreatingSupabase || !selectedOrg}
                  >
                    {isCreatingSupabase ? (
                      <>
                        <PxMark state="generating" size={14} className="mr-2" />
                        Création en cours…
                      </>
                    ) : (
                      <>
                        <Database className="mr-2 h-4 w-4" />
                        Créer un projet Supabase
                      </>
                    )}
                  </Button>

                  <p className="font-mono text-[11px] text-faint">
                    {isSupabaseOAuthConnected
                      ? "Le projet sera créé dans votre propre compte Supabase."
                      : "Un nouveau projet Supabase sera créé automatiquement avec les tables détectées dans votre code."}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 border border-border bg-secondary p-6 text-center">
                  <PxMark state="idle" size={24} />
                  <p className="text-[13px] text-muted-foreground">
                    Connectez votre compte Supabase pour créer des projets
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleConnectSupabase}
                    disabled={isConnectingSupabase}
                  >
                    {isConnectingSupabase ? (
                      <PxMark state="thinking" size={14} className="mr-2" />
                    ) : (
                      <Link2 className="mr-2 h-4 w-4" />
                    )}
                    Connecter mon compte Supabase
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Variables Tab */}
          <TabsContent value="vars" className="mt-6 space-y-4">
            <SectionLabel>Variables d'environnement</SectionLabel>

            <p className="text-[12px] text-muted-foreground">
              Gérez les variables disponibles dans votre projet
            </p>

            {/* Liste des variables existantes */}
            {isLoadingEnvVars ? (
              <div className="flex justify-center py-6">
                <PxMark state="thinking" size={20} />
              </div>
            ) : (
              <div className="space-y-2">
                {envVarsList.length === 0 ? (
                  <p className="py-4 text-center font-mono text-[11px] text-faint">
                    Aucune variable configurée
                  </p>
                ) : (
                  envVarsList.map((v) => (
                    <div key={v.id} className="flex items-center justify-between gap-3 border border-border bg-secondary px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <code className="font-mono text-[12px] font-medium text-foreground">{v.key}</code>
                        <span className="ml-2 font-mono text-[11px] text-muted-foreground">
                          {v.value ? `= ${v.value.substring(0, 30)}${v.value.length > 30 ? '…' : ''}` : '= ••••••••'}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-err"
                        aria-label="Supprimer la variable"
                        onClick={async () => {
                          try {
                            await deleteEnvVars(projectId, [v.id]);
                            toast.success('Variable supprimée');
                            loadEnvVars();
                          } catch (err) {
                            toast.error('Erreur lors de la suppression');
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Formulaire d'ajout */}
            <div className="h-px w-full bg-border" />
            <div className="flex gap-2">
              <Input
                placeholder="KEY_NAME"
                value={newEnvKey}
                onChange={(e) => setNewEnvKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                className="flex-1 font-mono text-[12px]"
              />
              <Input
                placeholder="Valeur"
                value={newEnvValue}
                onChange={(e) => setNewEnvValue(e.target.value)}
                type="password"
                className="flex-1 font-mono text-[12px]"
              />
              <Button
                onClick={async () => {
                  if (!newEnvKey.trim() || !newEnvValue.trim()) return;
                  try {
                    await createEnvVars(projectId, [{ key: newEnvKey, value: newEnvValue }]);
                    toast.success('Variable ajoutée');
                    setNewEnvKey('');
                    setNewEnvValue('');
                    loadEnvVars();
                  } catch (err) {
                    toast.error('Erreur lors de l\'ajout');
                  }
                }}
                disabled={!newEnvKey || !newEnvValue}
              >
                <Plus className="mr-1 h-4 w-4" />
                Ajouter
              </Button>
            </div>

            <p className="font-mono text-[11px] text-faint">
              Les variables Supabase sont automatiquement injectées quand vous connectez une base de données.
            </p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
